import { useState, useEffect } from 'react'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts'
import apiClient from '../services/apiClient'
import Layout from '../components/Layout'
import Spinner from '../components/Spinner'
import EmptyState from '../components/EmptyState'

const QUESTION_LABELS = {
  time_efficiency:     'Time Efficiency',
  quality_output:      'Quality & Output',
  clarity_detail:      'Clarity & Detail',
  risk_prevention:     'Risk Prevention',
  value_collaboration: 'Value & Collaboration',
  repeat_refine:       'Repeat & Refine',
}

const CHART_COLOR_PRIMARY  = '#6366f1'
const CHART_COLOR_SECONDARY = '#8b5cf6'

const ChartEmptyIcon = () => (
  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2"/>
    <path d="M7 16V11M11 16v-5M15 16V8M19 16V6"/>
  </svg>
)

function StatCard({ value, label }) {
  return (
    <div className="stat-card">
      <div className="stat-card-value">{value ?? '—'}</div>
      <div className="stat-card-label">{label}</div>
    </div>
  )
}

function RatingsBreakdownTable({ items, nameKey }) {
  return (
    <div className="table-container" style={{ marginTop: '1.25rem' }}>
      <table className="data-table analytics-table">
        <thead>
          <tr>
            <th>{nameKey === 'ai_tool' ? 'AI Tool' : nameKey === 'ai_model' ? 'AI Model' : 'Product Lifecycle'}</th>
            <th>Surveys</th>
            {Object.values(QUESTION_LABELS).map((l) => <th key={l}>{l}</th>)}
            <th>Overall</th>
          </tr>
        </thead>
        <tbody>
          {items.map((row) => (
            <tr key={row[nameKey]}>
              <td>{row[nameKey]}</td>
              <td>{row.survey_count}</td>
              {Object.keys(QUESTION_LABELS).map((k) => (
                <td key={k} className="analytics-cell-center">
                  {row.average_ratings?.[k] != null ? Number(row.average_ratings[k]).toFixed(1) : 'N/A'}
                </td>
              ))}
              <td className="analytics-cell-bold">
                {row.overall_average != null ? Number(row.overall_average).toFixed(2) : 'N/A'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function AveragesBarChart({ data, nameKey, color = CHART_COLOR_PRIMARY }) {
  const chartData = data.map((row) => ({
    name: row[nameKey],
    avg:  row.overall_average != null ? parseFloat(Number(row.overall_average).toFixed(2)) : 0,
  }))

  return (
    <div style={{ width: '100%', height: 280 }}>
      <ResponsiveContainer>
        <BarChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 48 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
          <XAxis
            dataKey="name"
            tick={{ fontSize: 12, fill: 'var(--text-secondary)' }}
            angle={-35}
            textAnchor="end"
            interval={0}
          />
          <YAxis domain={[0, 5]} tick={{ fontSize: 12, fill: 'var(--text-secondary)' }} />
          <Tooltip
            formatter={(v) => [`${v} / 5`, 'Avg rating']}
            contentStyle={{ background: 'var(--surface)', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)' }}
          />
          <Bar dataKey="avg" fill={color} radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}

export default function Analytics() {
  const [tab, setTab]               = useState('summary')
  const [summary, setSummary]       = useState(null)
  const [byTool, setByTool]         = useState([])
  const [byModel, setByModel]       = useState([])
  const [byProductLifecycle, setByProductLifecycle]       = useState([])
  const [experiments, setExperiments] = useState([])
  const [teams, setTeams]           = useState([])
  const [teamFilter, setTeamFilter] = useState('')
  const [selectedExp, setSelectedExp] = useState('')
  const [byExp, setByExp]           = useState(null)
  const [byExpLoading, setByExpLoading] = useState(false)
  const [loading, setLoading]       = useState(true)

  useEffect(() => {
    Promise.all([
      apiClient.get('/analytics/summary'),
      apiClient.get('/analytics/by-tool'),
      apiClient.get('/analytics/by-model'),
      apiClient.get('/analytics/by-product-lifecycle'),
      apiClient.get('/admin/experiments'),
      apiClient.get('/admin/teams'),
    ]).then(([sumRes, toolRes, modelRes, phaseRes, expRes, teamsRes]) => {
      setSummary(sumRes.data)
      setByTool(toolRes.data.items)
      setByModel(modelRes.data.items)
      setByProductLifecycle(phaseRes.data.items)
      setExperiments(expRes.data.items)
      setTeams(teamsRes.data.items ?? [])
    }).finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    if (!selectedExp) return
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setByExpLoading(true)
    apiClient
      .get(`/analytics/by-experiment/${selectedExp}`)
      .then((r) => setByExp(r.data))
      .finally(() => setByExpLoading(false))
  }, [selectedExp])

  const TABS = [
    { key: 'summary',       label: 'Summary' },
    { key: 'by-tool',       label: 'By AI Tool' },
    { key: 'by-model',      label: 'By AI Model' },
    { key: 'by-product-lifecycle', label: 'By Product Lifecycle' },
    { key: 'by-experiment', label: 'By AI Brief' },
  ]

  return (
    <Layout>
      <div className="page-header">
        <h1 className="page-title">Analytics</h1>
      </div>

      <div className="tab-bar">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            className={`tab-btn${tab === t.key ? ' active' : ''}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Summary ── */}
      {tab === 'summary' && (
        <div>
          {loading ? (
            <div className="loading-state">
              <Spinner size="md" />
              Loading analytics…
            </div>
          ) : summary ? (
            <>
              <div className="stat-cards">
                <StatCard value={summary.total_surveys}       label="Total surveys" />
                <StatCard value={summary.total_respondents}   label="Respondents" />
                <StatCard
                  value={summary.average_rating != null ? Number(summary.average_rating).toFixed(2) : null}
                  label="Average rating"
                />
                <StatCard value={summary.surveys_this_month}  label="This month" />
              </div>

              <div className="card" style={{ marginBottom: '1rem' }}>
                <div className="analytics-highlight-grid">
                  <div>
                    <span className="analytics-highlight-label">Top AI Tool</span>
                    <p className="analytics-highlight-value">{summary.top_ai_tool ?? '—'}</p>
                  </div>
                  <div>
                    <span className="analytics-highlight-label">Most Surveyed Product Lifecycle</span>
                    <p className="analytics-highlight-value">{summary.most_surveyed_product_lifecycle ?? '—'}</p>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <EmptyState
              icon={<ChartEmptyIcon />}
              title="No data yet"
              description="Submit surveys to start seeing analytics."
            />
          )}
        </div>
      )}

      {/* ── By Tool ── */}
      {tab === 'by-tool' && (
        <div>
          <h2 className="analytics-section-title">Average Rating by AI Tool</h2>
          {byTool.length > 0 ? (
            <>
              <div className="card analytics-chart-card">
                <AveragesBarChart data={byTool} nameKey="ai_tool" color={CHART_COLOR_PRIMARY} />
              </div>
              <RatingsBreakdownTable items={byTool} nameKey="ai_tool" />
            </>
          ) : (
            <EmptyState
              icon={<ChartEmptyIcon />}
              title="No tool data yet"
              description="Surveys that include AI tool selections will appear here."
            />
          )}
        </div>
      )}

      {/* ── By Model ── */}
      {tab === 'by-model' && (
        <div>
          <h2 className="analytics-section-title">Average Rating by AI Model</h2>
          {byModel.length > 0 ? (
            <>
              <div className="card analytics-chart-card">
                <AveragesBarChart data={byModel} nameKey="ai_model" color={CHART_COLOR_SECONDARY} />
              </div>
              <RatingsBreakdownTable items={byModel} nameKey="ai_model" />
            </>
          ) : (
            <EmptyState
              icon={<ChartEmptyIcon />}
              title="No model data yet"
              description="Surveys that include AI model selections will appear here."
            />
          )}
        </div>
      )}

      {/* ── By Product Lifecycle ── */}
      {tab === 'by-product-lifecycle' && (
        <div>
          <h2 className="analytics-section-title">Average Rating by Product Lifecycle</h2>
          {byProductLifecycle.length > 0 ? (
            <>
              <div className="card analytics-chart-card">
                <AveragesBarChart data={byProductLifecycle} nameKey="product_lifecycle_name" color={CHART_COLOR_SECONDARY} />
              </div>
              <RatingsBreakdownTable items={byProductLifecycle} nameKey="product_lifecycle_name" />
            </>
          ) : (
            <EmptyState
              icon={<ChartEmptyIcon />}
              title="No product lifecycle data yet"
              description="Surveys that include product lifecycle selections will appear here."
            />
          )}
        </div>
      )}

      {/* ── By AI Brief ── */}
      {tab === 'by-experiment' && (
        <div>
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1.5rem', alignItems: 'flex-end' }}>
            <div className="filter-group">
              <label htmlFor="analytics-team">Filter by Team</label>
              <select
                id="analytics-team"
                value={teamFilter}
                onChange={(e) => { setTeamFilter(e.target.value); setSelectedExp(''); setByExp(null) }}
                style={{ minWidth: 180 }}
              >
                <option value="">All Teams</option>
                {teams.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label htmlFor="analytics-exp">Select AI Brief</label>
              <select
                id="analytics-exp"
                value={selectedExp}
                onChange={(e) => { setSelectedExp(e.target.value); setByExp(null) }}
                style={{ minWidth: 280 }}
              >
                <option value="">— Select —</option>
                {experiments
                  .filter((e) => !teamFilter || e.team_id === Number(teamFilter))
                  .map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.team_name ? `[${e.team_name}] ` : ''}{e.experiment_name}
                    </option>
                  ))}
              </select>
            </div>
          </div>

          {byExpLoading && (
            <div className="loading-state">
              <Spinner size="md" />
              Loading…
            </div>
          )}

          {!selectedExp && !byExpLoading && (
            <EmptyState
              icon={<ChartEmptyIcon />}
              title="Select an AI Brief"
              description="Choose an AI Brief above to see its survey analytics."
            />
          )}

          {byExp && !byExpLoading && (
            <div>
              <div className="stat-cards">
                <StatCard value={byExp.survey_count} label="Surveys" />
                <StatCard
                  value={byExp.overall_average != null ? Number(byExp.overall_average).toFixed(2) : null}
                  label="Overall avg"
                />
                <StatCard value={byExp.respondents?.length ?? 0} label="Respondents" />
              </div>

              {byExp.product_lifecycles_recorded?.length > 0 && (
                <p className="analytics-phases-note">
                  <span className="analytics-phases-label">Product Lifecycles recorded: </span>
                  {byExp.product_lifecycles_recorded.join(', ')}
                </p>
              )}

              <div className="table-container">
                <table className="data-table analytics-table">
                  <thead>
                    <tr>
                      {Object.values(QUESTION_LABELS).map((l) => <th key={l}>{l}</th>)}
                      <th>Overall</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      {Object.keys(QUESTION_LABELS).map((k) => (
                        <td key={k} className="analytics-cell-center">
                          {byExp.average_ratings?.[k] != null
                            ? Number(byExp.average_ratings[k]).toFixed(1)
                            : 'N/A'}
                        </td>
                      ))}
                      <td className="analytics-cell-bold">
                        {byExp.overall_average != null ? Number(byExp.overall_average).toFixed(2) : 'N/A'}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </Layout>
  )
}
